import React, { memo, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  HStack,
  IconButton,
  Text,
  VStack,
  Highlight,
} from "@chakra-ui/react";
import colors from "app/colors";
import { useWavesurfer } from "@wavesurfer/react";
import moment from "moment";
import { PlayIcon, PauseIcon } from "modules/icons";
import useQueryString from "modules/hooks/use-query-string";
import WaveSurfer from "wavesurfer.js";

interface IProps {
  audioData?: string | File | null;
  fileName?: string;
  playIconSize?: number;
}

let currentlyPlayingWaveSurfer: WaveSurfer | null = null;

const stopCurrentlyPlayingWaveSurfer = (nextWaveSurfer?: WaveSurfer) => {
  if (
    currentlyPlayingWaveSurfer &&
    currentlyPlayingWaveSurfer !== nextWaveSurfer &&
    currentlyPlayingWaveSurfer.isPlaying()
  ) {
    currentlyPlayingWaveSurfer.pause();
  }
};

const AudioFilePlayer = ({ audioData, fileName, playIconSize }: IProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { qsMessageHighlights } = useQueryString();

  const { wavesurfer, isPlaying, currentTime, isReady } = useWavesurfer({
    container: containerRef,
    height: 24,
    barWidth: 2,
    barGap: 3,
    cursorColor: colors.gray50,
    cursorWidth: 2,
    waveColor: colors.gray30,
    progressColor: colors.black,
    //https://github.com/katspaugh/wavesurfer.js/discussions/3831
    //hopefully this will be fixed in the future
    //url: typeof audioData === "string" ? audioData : undefined,
    // fetchParams: {
    //   method: "GET",
    //   credentials: "include",
    // },
  });

  const seconds = useMemo(() => {
    if (!isReady) return "00:00";
    const duration = wavesurfer ? wavesurfer.getDuration() : 0;
    const timeDisplay = currentTime > 0 ? currentTime : duration;
    return moment.utc(timeDisplay * 1000).format("mm:ss");
  }, [currentTime, wavesurfer, isReady]);

  const onPlayClicked = useCallback(() => {
    if (!wavesurfer) return;
    if (!wavesurfer.isPlaying()) {
      stopCurrentlyPlayingWaveSurfer(wavesurfer);
      currentlyPlayingWaveSurfer = wavesurfer;
    }
    wavesurfer?.playPause();
  }, [wavesurfer]);

  useEffect(() => {
    if (!wavesurfer) return;

    const onPlay = () => {
      stopCurrentlyPlayingWaveSurfer(wavesurfer);
      currentlyPlayingWaveSurfer = wavesurfer;
    };

    const onStop = () => {
      if (currentlyPlayingWaveSurfer === wavesurfer) {
        currentlyPlayingWaveSurfer = null;
      }
    };

    const offPlay = wavesurfer.on("play", onPlay);
    const offPause = wavesurfer.on("pause", onStop);
    const offFinish = wavesurfer.on("finish", onStop);

    return () => {
      offPlay();
      offPause();
      offFinish();
      onStop();
    };
  }, [wavesurfer]);

  //Used to load the audio file when uploading a new audio file; othwerwise, the audio file is loaded via the url property above
  useEffect(() => {
    if (!audioData || !wavesurfer || isReady) return;
    if (typeof audioData !== "string") {
      wavesurfer?.loadBlob(audioData);
    } else {
      fetch(audioData, {
        method: "GET",
        credentials: "include",
      })
        .then((response) => response.blob())
        .then((blob) => wavesurfer?.loadBlob(blob));
    }
  }, [audioData, isReady, wavesurfer]);

  return (
    <HStack spacing={2} w="full" px={2} pt={1}>
      <IconButton
        icon={
          isPlaying ? (
            <PauseIcon fill={colors.blue} boxSize={playIconSize} />
          ) : (
            <PlayIcon fill={colors.blue} boxSize={playIconSize} />
          )
        }
        aria-label="play"
        variant="ghost"
        onClick={onPlayClicked}
      />
      <VStack w="full" spacing={1}>
        {fileName && (
          <Text
            w="full"
            color={colors.black}
            fontSize={14}
            noOfLines={1}
            wordBreak="break-all"
          >
            <Highlight
              query={qsMessageHighlights}
              styles={{ bg: colors.yellow }}
            >
              {fileName}
            </Highlight>
          </Text>
        )}
        <HStack w="full" spacing={0}>
          <Box ref={containerRef} w="full" pr={2} />
          <Text color={colors.grayMain} fontSize={12}>
            {seconds}
          </Text>
        </HStack>
      </VStack>
    </HStack>
  );
};

export default memo(AudioFilePlayer);
